import {
  GetCancellationRulesRequest,
  GetPropertyByIdRequest,
  GetRoomsByPropertyIdRequest,
  PropertyApi,
  SearchPropertiesByFilterRequest,
} from '@/api/generated/fetch-client';
import { WithSignal } from '@/types/typings';

import type {
  AccommodationCatalogCitiesResponseDto,
  AccommodationCatalogPageResponseDto,
  GetAccommodationCatalogCitiesRequest,
  GetAccommodationCatalogRequest,
} from '@/features/accommodation/types/catalog';

export class AccommodationApi {
  constructor(private readonly propertyApi: PropertyApi) {}

  async getCatalogCities(
    requestParameters: WithSignal<GetAccommodationCatalogCitiesRequest>,
  ): Promise<AccommodationCatalogCitiesResponseDto> {
    const { signal, countryCodes } = requestParameters;
    const query = new URLSearchParams();
    if (countryCodes) for (const code of countryCodes) query.append('countryCodes', code);
    const response = await fetch(
      `/api/catalog/accommodations/cities?${query.toString()}`,
      { method: 'GET', signal },
    );
    if (!response.ok) throw new Error(`Catalog cities error: ${response.status}`);
    return (await response.json()) as AccommodationCatalogCitiesResponseDto;
  }

  async getCatalogPage(
    requestParameters: WithSignal<GetAccommodationCatalogRequest>,
  ): Promise<AccommodationCatalogPageResponseDto> {
    const { signal, cityId, limit, cursor } = requestParameters;
    const query = new URLSearchParams();
    if (cityId) query.set('cityId', cityId);
    if (limit !== undefined) query.set('limit', String(limit));
    if (cursor !== undefined && cursor !== null) query.set('cursor', String(cursor));
    const response = await fetch(`/api/catalog/accommodations?${query.toString()}`, {
      method: 'GET',
      signal,
    });
    if (!response.ok) throw new Error(`Catalog page error: ${response.status}`);
    return (await response.json()) as AccommodationCatalogPageResponseDto;
  }

  async getCancellationRules(requestParameters: WithSignal<GetCancellationRulesRequest>) {
    const { signal, ...args } = requestParameters;
    return this.propertyApi.getCancellationRules(args, { signal });
  }

  async searchPropertiesByFilter(
    requestParameters: WithSignal<SearchPropertiesByFilterRequest>,
  ) {
    const { signal, ...args } = requestParameters;
    return this.propertyApi.searchPropertiesByFilter(args, { signal });
  }

  async getPropertyById(requestParameters: WithSignal<GetPropertyByIdRequest>) {
    const { signal, ...args } = requestParameters;
    return this.propertyApi.getPropertyById(args, { signal });
  }

  async getRoomsByPropertyId(requestParameters: WithSignal<GetRoomsByPropertyIdRequest>) {
    const { signal, ...args } = requestParameters;
    return this.propertyApi.getRoomsByPropertyId(args, { signal });
  }
}
